import { useEffect, useState } from "react";
import { useLocalization } from "../../context/LocalizationContext";
import { useVehicleContext } from "../../context/VehicleContext";
import { UserRole } from "../../shared/Enums/UserRole";
import { getCurrencySymbol } from "../../utils/currencySymbols";
import LoadingOverlay from "../common/LoadingOverlay";
import { useNavigate, useLocation } from "react-router-dom";
import apiManager from "../../api/apiManager";
import API_ENDPOINTS from "../../api/apiEndpoints";
import ServiceOrderStatus from "../../shared/Enums/ServiceOrderStatus";
import ServiceAction from "../../shared/Enums/ServiceAction";
import { useSelectedDealership } from "../../hooks/useLocalStorage";
import getUserRoleFromJWT from "../../shared/helpers/getUserRoleFromJWT";
import {
  showSuccessMessage,
  showErrorMessage,
} from "../../shared/Services/toasterService";
import {
  showLoader,
  hideLoader,
} from "../../shared/Services/Loader/loaderService";
import { formatPhoneNumber } from "../../shared/helpers/formUtils";
import Swal from "sweetalert2";
import InfoCards from "./common/InfoCards";
import PriceSummary from "./common/PriceSummary";
import CustomerApprovalModal from "./modals/CustomerApprovalModal";
import SentConfirmationModal from "./modals/SentConfirmationModal";
import useServiceFetching from "../../hooks/useServiceFetching";
import useServiceTotals from "../../hooks/useServiceTotals";
import { normalizeCurrencyPrice } from "../../utils/price";
import ServiceListDisplay from "./common/ServiceListDisplay";
import ServiceTableRow from "./common/ServiceTableRow";

const SelectServicesPage = () => {
  const { t } = useLocalization();
  const ctx = useVehicleContext();
  const {
    customerId,
    vehicleId,
    vehicle: propVehicle,
    customer: propCustomer,
    dealershipId,
    status: externalStatus,
    setStatus: externalSetStatus,
    selectedServices: externalSelectedServices,
    setSelectedServices: externalSetSelectedServices,
    repairOrder,
    setRepairOrder,
  } = ctx;
  const location = useLocation();
  const [internalStatus, internalSetStatus] = useState(ServiceOrderStatus.NEW);
  const status = externalStatus !== undefined ? externalStatus : internalStatus;
  const setStatus =
    externalSetStatus !== undefined ? externalSetStatus : internalSetStatus;
  const [internalSelectedServices, internalSetSelectedServices] = useState([]);
  const selectedServices =
    externalSelectedServices !== undefined
      ? externalSelectedServices
      : internalSelectedServices;
  const setSelectedServices =
    externalSetSelectedServices !== undefined
      ? externalSetSelectedServices
      : internalSetSelectedServices;
  const [showCustomerApprovalModal, setShowCustomerApprovalModal] =
    useState(false);
  const [modalData, setModalData] = useState(null);
  const [showSentConfirmationModal, setShowSentConfirmationModal] =
    useState(false);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [selectedDealershipId] = useSelectedDealership();
  const navCustomerId = location?.state?.customerId;
  const existingRepairOrderId = location?.state?.repairOrderFromInProgress;
  const id = navCustomerId || customerId || null;
  const [activeTab, setActiveTab] = useState("oem");
  const navigate = useNavigate();
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("USD");
  const effectiveDealershipId = selectedDealershipId || dealershipId;
  const [customer, setCustomer] = useState(propCustomer || null);
  const [vehicle, setVehicle] = useState(
    propVehicle && Object.keys(propVehicle).length > 0 ? propVehicle : null,
  );
  const { services, isLoading: servicesLoading } = useServiceFetching(
    vehicle?.id || vehicleId,
    effectiveDealershipId,
    t,
  );
  const { calculateServiceTypeTotal } = useServiceTotals(selectedServices);
  // Add loading state for services
  const [servicesLoaded, setServicesLoaded] = useState(false);

  // Filter out deleted services (status 2) for display
  const activeSelectedServices = selectedServices.filter((s) => s.status !== 2);
  const subtotalAmount = activeSelectedServices.reduce(
    (sum, service) => sum + (normalizeCurrencyPrice(service.price) || 0),
    0,
  );
  const taxRate = Number(dealership?.tax) || 0;
  const taxAmount = subtotalAmount * (taxRate / 100);
  const totalWithTax = subtotalAmount + taxAmount;

  const isServiceSelected = (service) => {
    return selectedServices.some(
      (s) =>
        s.status !== 2 &&
        ((s.id && service.id && s.id === service.id) ||
          (s.serviceId &&
            service.serviceId &&
            s.serviceId === service.serviceId)),
    );
  };

  const handlePriceChange = (serviceId, newPrice) => {
    const updatedServices = selectedServices.map((s) => {
      if (s.id === serviceId || s.serviceId === serviceId) {
        return {
          ...s,
          price: normalizeCurrencyPrice(newPrice),
        };
      }
      return s;
    });

    setSelectedServices(updatedServices);
  };

  const handleServiceSelect = (service, type) => {
    // Check if service exists in the list (including deleted ones)
    const existingServiceIndex = selectedServices.findIndex(
      (s) =>
        (s.id && service.id && s.id === service.id) ||
        (s.serviceId && service.serviceId && s.serviceId === service.serviceId),
    );

    if (existingServiceIndex === -1) {
      // Service doesn't exist at all, add it as new (status 0)
      const newService = {
        ...service,
        id: service.id || `service-${Date.now()}`,
        type: type,
        price: normalizeCurrencyPrice(service.price),
        status: 0,
      };
      setSelectedServices([...selectedServices, newService]);
    } else {
      // Service exists, toggle its status
      const existingService = selectedServices[existingServiceIndex];
      const updatedServices = selectedServices.map((s, index) => {
        if (index === existingServiceIndex) {
          // If deleted (status 2), reactivate to its previous status (0 for new, 1 for existing)
          // If active, mark as deleted (status 2)
          return {
            ...s,
            type: s.status === 2 ? type : s.type,
            status: s.status === 2 ? (existingService.serviceId ? 1 : 0) : 2,
          };
        }
        return s;
      });
      setSelectedServices(updatedServices);
    }
  };

  const handleRemoveService = (serviceId) => {
    Swal.fire({
      title: t("confirm_removal"),
      text: t("confirm_remove_service"),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t("cancel"),
      confirmButtonText: t("delete"),
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      width: "280px",
      padding: "0.5em",
      iconColor: "#f39c12",
      customClass: {
        popup: "swal2-small-popup",
        title: "swal2-small-title",
        icon: "swal2-small-icon",
        confirmButton: "btn btn-danger btn-sm",
        cancelButton: "btn btn-cancel !important",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedServices = selectedServices.map((service) => {
          if (service.id === serviceId || service.serviceId === serviceId) {
            return { ...service, status: 2 };
          }
          return service;
        });
        setSelectedServices(updatedServices);
      }
    });
  };

  const loadRepairOrderItems = async (forceReload = false) => {
    const idToFetch = existingRepairOrderId || repairOrder?.data;
    if (!idToFetch) {
      setServicesLoaded(true);
      return;
    }
    // Prevent reloading if services are already loaded and not forcing a reload
    if (servicesLoaded && !forceReload) {
      return;
    }
    setServicesLoaded(false);
    try {
      const payload = { repairOrderId: idToFetch };
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.REPAIR_ORDER_ITEM,
        payload,
      );
      if (response.status === 200 && response.data) {
        const backendServices = response.data.map((item) => ({
          id: item.serviceId || `service-${Date.now()}`,
          serviceId: item.serviceId,
          serviceName: item.serviceName,
          description: item.serviceDescription,
          type: item.serviceCategory,
          price: normalizeCurrencyPrice(item.price),
          status: item.status !== undefined ? item.status : 1,
          ...item,
        }));
        setSelectedServices(backendServices);
      }
    } catch (err) {
      showErrorMessage("Failed to fetch repair order items");
    } finally {
      hideLoader();
      setServicesLoaded(true);
    }
  };
  useEffect(() => {
    loadRepairOrderItems();
  }, [existingRepairOrderId, repairOrder]);

  useEffect(() => {
    if (propVehicle && Object.keys(propVehicle).length > 0) {
      if (!vehicle || propVehicle.id !== vehicle.id) {
        setVehicle(propVehicle);
      }
    }
  }, [propVehicle, vehicle]);

  useEffect(() => {
    if (!vehicle && vehicleId) {
      (async () => {
        try {
          const res = await apiManager.post(
            API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
            { id: vehicleId },
          );
          if (res.status === 200 && res.data) {
            setVehicle(res.data);
          }
        } catch (err) {
          showErrorMessage(t("error_fetch_vehicle"));
        }
      })();
    }
  }, [vehicle, vehicleId, t]);

  const handleOk = async () => {
    if (!modalData || !selectedMethod) return;
    try {
      showLoader();
      if (selectedMethod === "email") {
        const emailPayload = {
          toEmail: modalData.email,
          subject: t("customer_approval"),
          dealershipId: effectiveDealershipId,
          repairOrderId: modalData.repairOrderId,
        };
        await apiManager.post(API_ENDPOINTS.SEND_EMAIL, emailPayload, {
          headers: { "Content-Type": "application/json" },
        });
        showSuccessMessage(t("success_email_sent"));
        setShowSentConfirmationModal(true);
      }
      if (selectedMethod === "sms") {
        const smsPayload = {
          toNumber: formatPhoneNumber(modalData.phoneNumber),
          subject: t("customer_approval"),
          dealershipId: effectiveDealershipId,
          repairOrderId: modalData.repairOrderId,
        };
        await apiManager.post(API_ENDPOINTS.SEND_SMS, smsPayload, {
          headers: { "Content-Type": "application/json" },
        });
        showSuccessMessage(t("success_sms_sent"));
        setShowSentConfirmationModal(true);
      }
      if (selectedMethod === "self") {
        const statusPayload = {
          repairOrderId: modalData.repairOrderId,
          statusCode: ServiceOrderStatus.SIGNED,
        };
        const response = await apiManager.post(
          API_ENDPOINTS.DEALER_ORDER_STATUS,
          statusPayload,
        );
        if (response.status === 200) {
          if (location.state.fromRedirect) {
            navigate(location.pathname, {
              state: {
                ...location.state,
                status: ServiceOrderStatus.SIGNED,
                goToStep4: true,
                goToStep3: false,
              },
              replace: true,
            });
          }
          setStatus(ServiceOrderStatus.SIGNED);
          showSuccessMessage(t("order_signed_successfully"));
        }
      }
      setShowCustomerApprovalModal(false);
      await loadRepairOrderItems(true); // Force reload after approval - same behavior as InProgressPage
    } catch (err) {
      showErrorMessage(err);
    } finally {
      hideLoader();
    }
  };

  const handleApprove = async () => {
    // Filter out deleted services for validation
    const activeServices = selectedServices.filter((s) => s.status !== 2);
    if (!customer || !vehicle || !dealership || activeServices.length === 0) {
      showErrorMessage(t("error_missing_required_or_no_services"));
      return;
    }
    setSelectedMethod("");
    try {
      showLoader();
      if (existingRepairOrderId || repairOrder) {
        const updatePayload = {
          repairOrderId: existingRepairOrderId || repairOrder.data,
          dealershipId: dealership.id,
          customerId: customer.id,
          customerVehicleId: vehicle.id,
          milesOk: true,
          dateOfService: new Date(),
          statusCode: status,
          action: ServiceAction.ADDED,
          items: selectedServices.map((s) => ({
            serviceCategory: s.type,
            serviceName: s.serviceName || s.displayName,
            serviceId: s.serviceId || s.id,
            serviceDescription: s.description,
            serviceBucket: s.serviceBucket,
            laborHours: s.laborHours,
            price: normalizeCurrencyPrice(s.price),
            isMileageBased: s.isMileageBased || false,
            minMileage: s.minMileage,
            maxMileage: s.maxMileage,
            source: s.source,
            status: s.status !== undefined ? s.status : 0,
          })),
        };

        const response = await apiManager.post(
          API_ENDPOINTS.REPAIR_ORDER_UPDATE,
          updatePayload,
        );
        if (response.data) {
          showSuccessMessage(t("success_service_updated"));
          setModalData({
            email: customer.email,
            phoneNumber: formatPhoneNumber(customer.phoneNumber),
            dealershipId: dealershipId,
            repairOrderId: existingRepairOrderId,
          });
          setShowCustomerApprovalModal(true);
        } else {
          throw new Error(
            response.data.message || "Failed to update repair order",
          );
        }
      } else {
        // For new repair orders, only send active services (not deleted ones)
        const activeServices = selectedServices.filter((s) => s.status !== 2);
        const payload = {
          dealershipId: dealership.id,
          customerId: customer.id,
          customerVehicleId: vehicle.id,
          odometer: vehicle.odometer,
          milesOk: true,
          dateOfService: new Date(),
          statusCode: status,
          action: ServiceAction.ADDED,
          items: activeServices.map((s) => ({
            serviceCategory: s.type,
            serviceName: s.serviceName || s.displayName,
            serviceId: s.serviceId || s.id,
            serviceDescription: s.description,
            serviceBucket: s.serviceBucket,
            laborHours: s.laborHours,
            price: normalizeCurrencyPrice(s.price),
            isMileageBased: s.isMileageBased || false,
            minMileage: s.minMileage,
            maxMileage: s.maxMileage,
            source: s.source,
            status: s.status !== undefined ? s.status : 0,
          })),
        };

        const res = await apiManager.post(
          API_ENDPOINTS.REPAIR_ORDER_CREATE,
          payload,
        );
        if (res.status === 200 && res.data) {
          setRepairOrder(res.data);
          setModalData({
            email: customer.email,
            phoneNumber: formatPhoneNumber(customer.phoneNumber),
            dealershipId: dealershipId,
            repairOrderId: res.data.data,
          });
          showSuccessMessage(t("order_created_successfully"));
          setShowCustomerApprovalModal(true);
        } else {
          showErrorMessage(t("approval_failed"));
        }
      }
    } catch (err) {
      showErrorMessage(err);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (!effectiveDealershipId) return;

    const fetchDealership = async () => {
      try {
        showLoader();
        const response = await apiManager.post(API_ENDPOINTS.DEALER_GET_ID, {
          id: effectiveDealershipId,
        });
        if (response.status == 200) {
          const data = response.data;
          setDealership(data);
          setCurrencyCode(data.currencyCode || "USD");
        } else {
          setDealership(null);
          setCurrencyCode("USD");
          showErrorMessage(t("error_fetch_dealership"));
        }
      } catch (err) {
        setDealership(null);
        setCurrencyCode("USD");
        showErrorMessage(t("error_fetch_dealership"));
      } finally {
        hideLoader();
      }
    };

    fetchDealership();
  }, [effectiveDealershipId, t]);

  useEffect(() => {
    if (customer && vehicle) {
      setLoading(false);
      return;
    }

    if (!id) return;

    const fetchCustomerAndVehicle = async () => {
      setLoading(true);
      setError(null);
      try {
        showLoader();
        const response = await apiManager.post(
          API_ENDPOINTS.CUSTOMER_GET_BY_ID,
          { id },
        );
        if (response.status == 200) {
          const data = response.data;
          setCustomer(data);

          if (!vehicle && vehicleId) {
            const newResponse = await apiManager.post(
              API_ENDPOINTS.CUSTOMER_GET_VEHICLE_BY_ID,
              { id: vehicleId },
            );
            if (newResponse.status == 200) {
              setVehicle(newResponse.data);
            } else {
              showErrorMessage(t("error_fetch_vehicle"));
            }
          }
        } else {
          showErrorMessage(t("error_fetch_customer"));
        }
      } catch (err) {
        showErrorMessage(err);
      } finally {
        hideLoader();
        setLoading(false);
      }
    };

    fetchCustomerAndVehicle();
  }, [id, customer, vehicle, vehicleId, t]);

  const isLoading = loading || servicesLoading;
  const serviceError = error || services[activeTab]?.error;

  if (serviceError)
    return <div className="alert alert-danger">{serviceError}</div>;

  return (
    <div>
      {isLoading && <LoadingOverlay />}
      <div className="select-services-content">
        <div className="container-fluid">
          <InfoCards t={t} vehicle={vehicle} customer={customer} />
          <div className="col-md-12">
            <div className="card mb-4">
              <PriceSummary
                t={t}
                currencyCode={currencyCode}
                calculateServiceTypeTotal={calculateServiceTypeTotal}
              />
              <div className="card-body">
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-lg-6 tab-content-card">
                      <ServiceListDisplay
                        services={services}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        isServiceSelected={isServiceSelected}
                        onServiceSelect={handleServiceSelect}
                        currencyCode={currencyCode}
                        selectDisabled={
                          status !== ServiceOrderStatus.NEW &&
                          status !== ServiceOrderStatus.IN_PROGRESS &&
                          status !== ServiceOrderStatus.APPROVAL_EXPIRED &&
                          status !== ServiceOrderStatus.DECLINED
                        }
                        t={t}
                      />
                    </div>
                    <div className="col-lg-6 selected-card">
                      <h2>{t("selected")}</h2>
                      <table className="table">
                        <tbody>
                          {activeSelectedServices.length > 0 ? (
                            activeSelectedServices.map((service, index) => (
                              <ServiceTableRow
                                key={`selected-${service.id || index}`}
                                service={service}
                                index={index}
                                currencyCode={currencyCode}
                                onRemove={() =>
                                  handleRemoveService(
                                    service.id || service.serviceId,
                                  )
                                }
                                onPriceChange={handlePriceChange}
                                canRemove={
                                  status === ServiceOrderStatus.NEW ||
                                  status === ServiceOrderStatus.IN_PROGRESS ||
                                  status === ServiceOrderStatus.APPROVAL_EXPIRED
                                }
                                t={t}
                              />
                            ))
                          ) : (
                            <tr className="no-services-text">
                              <td colSpan="4" className="text-center py-3">
                                {t("no_services_selected")}
                              </td>
                            </tr>
                          )}
                          {activeSelectedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{ borderTop: "2px solid #dee2e6" }}
                            >
                              <td></td>
                              <td className="text-start text-muted">
                                {t("tax")} ({taxRate}%)
                              </td>
                              <td className="mulish-extra-bold text-muted">
                                {getCurrencySymbol(currencyCode)}
                                {taxAmount.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                          {activeSelectedServices.length > 0 && (
                            <tr
                              className="total-row"
                              style={{
                                borderTop: "1px solid #dee2e6",
                                fontSize: "1.05rem",
                              }}
                            >
                              <td></td>
                              <td className="text-start fw-bold">
                                {t("total")}
                              </td>
                              <td className="mulish-extra-bold">
                                {getCurrencySymbol(currencyCode)}
                                {totalWithTax.toFixed(2)}
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer bg-gray d-flex justify-content-center align-items-center gap-2">
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={handleApprove}
                    disabled={activeSelectedServices.length === 0}
                  >
                    {t("approve")}
                  </button>

                  <button
                    className="btn btn-cancel"
                    onClick={() => {
                      const userRole = getUserRoleFromJWT();
                      navigate(
                        userRole === UserRole.DEALER_ADMIN
                          ? "/admin-dashboard"
                          : "/service-desk-dashboard",
                      );
                    }}
                  >
                    {t("cancel")}
                  </button>
                </>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomerApprovalModal
        show={showCustomerApprovalModal}
        modalData={modalData}
        showSmsOption={Boolean(customer?.smsConsent)}
        selectedMethod={selectedMethod}
        setSelectedMethod={setSelectedMethod}
        onOk={async () => {
          await handleOk();
          if (selectedMethod === "self") {
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            setShowCustomerApprovalModal(false);
          }
        }}
        onCancel={() => setShowCustomerApprovalModal(false)}
        t={t}
      />
      <SentConfirmationModal
        show={showSentConfirmationModal}
        selectedMethod={selectedMethod}
        onGoToDashboard={() => {
          setShowSentConfirmationModal(false);
          const userRole = getUserRoleFromJWT();
          navigate(
            userRole === UserRole.DEALER_ADMIN
              ? "/admin-dashboard"
              : "/service-desk-dashboard",
          );
        }}
        t={t}
      />
    </div>
  );
};

export default SelectServicesPage;
